// Power BI to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const powerBiPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Power BI to Amazon Quick',
    intro: 'The reasons we hear most from Power BI customers running on AWS.',
    cards: [
      { title: 'Your data is already on AWS', description: 'Stop pulling Redshift, S3, and Athena data through gateways into imported models. Quick reads it where it lives.' },
      { title: 'AI agents on the same data', description: 'Once reports are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment instead of a separate capacity bill.' },
      { title: 'Security in one place', description: 'RLS roles and workspace access move to IAM, SSO, and row-level security on AWS.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Power BI metadata',
    title: 'We read your Power BI tenant through its APIs',
    intro: 'Instead of opening every .pbix, we pull metadata straight from Power BI. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Workspaces, reports, and dashboards', from: 'Power BI REST and Admin APIs', use: 'Build the full inventory and group content by workspace and business area' },
      { what: 'Semantic models, tables, and columns', from: 'Admin scanner API or XMLA endpoint', use: 'Size the data landing in Amazon S3 or Redshift and rebuild datasets on Quick' },
      { what: 'DAX measures and calculated columns', from: 'Admin scanner API or XMLA endpoint', use: 'Convert calculations to Quick calculated fields and spot duplicate measures' },
      { what: 'Power Query (M) and dataflows', from: 'Model expressions and Dataflows API', use: 'Map lineage end to end and rebuild as AWS Glue or Redshift SQL' },
      { what: 'RLS roles and workspace access', from: 'Model roles and Admin APIs', use: 'Recreate the same row-level security and permissions on Amazon Quick' },
      { what: 'Activity events and refresh history', from: 'Activity Events API', use: 'Find the most-used and unused reports to rank waves and plan refresh schedules' },
      { what: 'Data sources and gateways', from: 'Gateways and Datasources APIs', use: 'Plan connectivity for every source system' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every report traced back through its model and Power Query steps to the source system.' },
        { title: 'Complexity score per report', description: 'DAX, M transforms, and RLS roles scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by activity events, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Power BI through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Power BI admin registers a service principal with read-only admin API access. Nothing in Power BI changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Power BI migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every report.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled through the Power BI APIs: workspaces, reports, models, DAX, Power Query, RLS, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Reports grouped into waves by usage, shared models, and workspace. Unused reports are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Power Query rebuilt as AWS pipelines, DAX converted to calculated fields, and report pages rebuilt as visuals.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated report compared with its Power BI original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Power BI reports today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Power BI reports used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
